import { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { useForm } from "hooks/useForm";
import {
  fieldValidations,
  formatToDate,
  initialValues,
} from "../CustomerOrderForm";
import { convertToMapById } from "helpers/convertToMap";
import { orderArrayV2 } from "helpers/orderArray";
import { cleanNumber } from "helpers/cleanNumbers";
import { formatNumber } from "helpers/getNumberFormats";
import { validateObject } from "helpers/validateObject";
import {
  newCustomerService,
  getSchedules,
  getCustomerProducts,
  getCreditAccount,
  selectOnlyCustomerAccount,
  checkCoupon,
} from "redux/actions/customers/customersAction";
import { loadStationConfig } from "redux/actions/configSync/configSyncAction";
import { fetchStationUnits } from "redux/actions/coveragesAssign/coveragesAssignActions";
import {
  checkCreditUnlockSettings,
  cleanExists,
} from "redux/actions/creditsUnlock/creditsUnlockActions";

const { idAccount, ...nInitialValues } = initialValues;
const { idAccount: idAcc, ...nFieldValidations } = fieldValidations;

const isTimeInRange = (time, ranges) => {
  const toMinutes = (t) => {
    const [hours, minutes] = t.split(":").map(Number);
    return hours * 60 + minutes;
  };
  const timeInMinutes = toMinutes(time);
  for (const range of ranges) {
    const start = toMinutes(range.start);
    const end = toMinutes(range.end);
    if (timeInMinutes >= start && timeInMinutes <= end) return range;
  }
  return null;
};

export { nInitialValues, nFieldValidations };

export const useOrderForm = ({ account, showAccountSelector, onClose }) => {
  const dispatch = useDispatch();
  const { customer, schedules, products, myCallID, amazonConnectCall, paymentMethods, appliedCoupon } =
    useSelector(({ customersReducer }) => customersReducer);
  const { stationsList } = useSelector(({ companies }) => companies);
  const { isLoadingComp } = useSelector(({ search }) => search);
  const { stationUnits } = useSelector((state) => state.coveragesAssign);
  const { units } = useSelector(({ unitsReducer }) => unitsReducer);
  const { brands } = useSelector(({ brandsReducer }) => brandsReducer);
  const { token } = useSelector(({ login }) => login);
  const { requestCredit } = useSelector(({ creditsUnlock }) => creditsUnlock);

  const { id, alias, idStation, localPhone, type, address, idCustomer } =
    account || {};

  const {
    inputValues,
    handleInputOnChange,
    handleNewValues,
    cleanForm,
    fieldErrors,
    setFieldErrors,
    runValidates,
    handleNumberInputOnChange,
    checkboxOnChange,
  } = useForm(nInitialValues, nFieldValidations);

  const { date, schedule, product, quantity, price } = inputValues;

  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [brandOptions, setBrandOptions] = useState([]);
  const [brandLocked, setBrandLocked] = useState(false);
  const [unitsOpt, setUnitsOpt] = useState([]);
  const [dates, setDates] = useState([]);
  const [validatedSchedules, setValidatedSchedules] = useState([]);
  const [notFunds, setNotFunds] = useState(false);
  const [validateType, setValidateType] = useState(false);

  const creditAccount = account?.creditAccounts;

  const disabledCredit = useMemo(() => {
    if (account?.errorCredit) return true;
    if (!creditAccount || requestCredit) return false;
    const isSuspended = account?.isCreditSuspended;
    const noCreditAvailable = creditAccount?.creditAvailability === 0;
    const isPastSuspensionDate = moment(creditAccount?.suspendedDate).isSameOrBefore(moment(), "day");
    return isSuspended || noCreditAvailable || notFunds || isPastSuspensionDate;
  }, [account, creditAccount, requestCredit, notFunds]);

  const limitDays =
    schedules.length > 0
      ? {
          min: schedules[0].value,
          max: schedules[schedules.length - 1].value,
        }
      : {};

  // isTimeInRange extracted outside hook (see top of file)

  const handleDates = useCallback(
    ({ target }, stationConfigRef) => {
      const datesSelect = validatedSchedules.find(
        (sch) => sch.value === target.value,
      );
      if (!datesSelect) return;

      let hasSchedule = false;
      if (inputValues.schedule)
        hasSchedule = isTimeInRange(
          inputValues.schedule.split(" - ")[0],
          datesSelect.schedules,
        );

      setDates(datesSelect.schedules);
      handleNewValues({
        ...inputValues,
        date: target.value,
        ...(!stationConfigRef?.forceSchedule || !hasSchedule
          ? { schedule: "" }
          : { schedule: hasSchedule?.value }),
      });

      return { hasSuggestedSchedule: !!hasSchedule };
    },
    [validatedSchedules, inputValues, handleNewValues],
  );

  const handleByLts = useCallback(
    (e) => {
      const newQuantity = cleanNumber(e.target.value);
      let fQuantity = parseFloat(newQuantity);
      let findProduct = products.find((prod) => prod.id === product);

      if (newQuantity > 0) {
        if (!findProduct) {
          handleNewValues({
            ...inputValues,
            quantity: formatNumber(newQuantity),
            price: "----",
          });
          return;
        }
        handleNewValues({
          ...inputValues,
          quantity: formatNumber(newQuantity),
          price: formatNumber(
            fQuantity * (findProduct?.price * findProduct?.capacity),
          ),
        });
        handleNumberInputOnChange(e);
      } else {
        handleNewValues({ ...inputValues, quantity: "", price: "" });
      }
    },
    [products, product, inputValues, handleNewValues, handleNumberInputOnChange],
  );

  const handleByPrice = useCallback(
    (e) => {
      const newPrice = cleanNumber(e.target.value);
      let productInitial = products[0];
      if (newPrice > 0) {
        handleNewValues({
          ...inputValues,
          quantity: formatNumber(newPrice / productInitial.price),
          price: formatNumber(newPrice),
        });
        handleNumberInputOnChange(e);
      } else {
        handleNewValues({ ...inputValues, quantity: 0, price: "" });
      }
    },
    [products, inputValues, handleNewValues, handleNumberInputOnChange],
  );

  const handleObservations = useCallback(() => {
    const prdt = products.find((prod) => prod.id === product);
    handleNewValues({
      ...inputValues,
      observations: `${type} | ${quantity} ( ${prdt?.product} ) \nTel: ${localPhone} \nReferencias:  ${address?.reference ? address.reference : ""}`,
    });
  }, [products, product, inputValues, type, quantity, localPhone, address, handleNewValues]);

  const handleAccountSelect = useCallback(
    (selectedAcc) => {
      dispatch(selectOnlyCustomerAccount(selectedAcc));
      setUnitsOpt([]);
      setSelectedUnit(null);
      handleNewValues({ ...inputValues, idUnit: null });
      if (
        selectedAcc !== null &&
        validateObject(selectedAcc) &&
        selectedAcc?.code?.startsWith("ERP-") &&
        !Object.prototype.hasOwnProperty.call(selectedAcc, "creditAccounts")
      ) {
        dispatch(getCreditAccount(selectedAcc.id));
      }
    },
    [dispatch, inputValues, handleNewValues],
  );

  // Fetch data when account changes in selector mode
  useEffect(() => {
    if (showAccountSelector && validateObject(account)) {
      dispatch(getSchedules(account.idStation));
      dispatch(
        getCustomerProducts(account.id, {
          stations: stationsList,
          customerIdStation: account.idStation,
        }),
      );
    }
  }, [account, showAccountSelector]);

  // Load station config and units
  useEffect(() => {
    if (!account || !idStation) return;
    dispatch(loadStationConfig(idStation, "UNIT_SUGGESTION"));
    dispatch(fetchStationUnits(idStation));
  }, [token, account, idStation]);

  // Build brand options
  useEffect(() => {
    dispatch(cleanExists());
    if (brands && idStation) {
      const uniqueBrands = Object.values(brands)
        .filter((brand) => brand.idStation === idStation)
        .reduce((acc, brand) => {
          if (!acc.find((b) => b.idBrand === brand.idBrand)) acc.push(brand);
          return acc;
        }, [])
        .sort((a, b) => a.name.localeCompare(b.name));
      const options = [{ name: "Todos", idBrand: "todos" }, ...uniqueBrands];
      setBrandOptions(options);
      dispatch(checkCreditUnlockSettings(id, idStation));
    }
  }, [brands, idStation, alias]);

  // Auto-select brand from call data
  useEffect(() => {
    // Si ya hay marca seleccionada, no reasignar (el agente puede estar trabajando con datos de una llamada previa o capturados manualmente)
    if (brandOptions.length > 0 && !selectedBrand) {
      const contactData = JSON.parse(
        localStorage.getItem("contact-global") || "{}",
      );
      const idBrand = contactData.id_brand || amazonConnectCall?.id_brand;
      const hasActiveCall =
        myCallID &&
        amazonConnectCall?.callID &&
        String(myCallID) === String(amazonConnectCall.callID);

      let defaultBrand = brandOptions[0];
      if (idBrand && hasActiveCall) {
        const foundBrand = brandOptions.find((b) => b.idBrand === idBrand);
        if (foundBrand) {
          defaultBrand = foundBrand;
          setBrandLocked(true);
        } else {
          setBrandLocked(false);
        }
      } else {
        setBrandLocked(false);
      }
      setSelectedBrand(defaultBrand);
    }
  }, [brandOptions, amazonConnectCall, myCallID]);

  // Build unit options
  useEffect(() => {
    if (!account || !idStation || !type) return;

    const toMap = convertToMapById(
      stationUnits
        .concat(Array.isArray(units) ? units : [])
        .map((obj) => ({
          ...obj,
          label: `${obj.number} - ${obj.type} - ${obj.driver}`,
          value: obj.id,
        })),
    );
    const sort = orderArrayV2(toMap, "label");

    let filteredUnits = sort.filter(
      (unit) =>
        unit.idStation === idStation &&
        (type === "ESTACIONARIO"
          ? unit.type === "ESTACION" || unit.type === type
          : unit.type === type),
    );

    if (selectedBrand && selectedBrand.idBrand !== "todos") {
      filteredUnits = filteredUnits.filter(
        (unit) => unit.idBrand === selectedBrand.idBrand,
      );
    }

    const shouldClearUnit =
      selectedUnit && !filteredUnits.find((unit) => unit.id === selectedUnit.id);

    setUnitsOpt(filteredUnits);

    if (shouldClearUnit) {
      setSelectedUnit(null);
      handleNewValues({ ...inputValues, idUnit: null });
    }
  }, [stationUnits, units, selectedBrand, account?.id, idStation, type]);

  // Validate schedules
  useEffect(() => {
    const today = formatToDate();
    const validated = schedules.map((sch) => {
      if (sch.value === today) {
        const currentTime = new Date().getHours();
        const filteredSchedules = sch.schedules.filter(
          (hours) => parseInt(hours.end.slice(0, 2)) > currentTime,
        );
        const newSchedules =
          filteredSchedules.length === 0 && sch.schedules.length > 0
            ? [sch.schedules[sch.schedules.length - 1]]
            : filteredSchedules;
        return { ...sch, schedules: newSchedules };
      }
      return sch;
    });

    setValidatedSchedules(validated);
    const todaySchedule = validated.find((s) => s.value === today);
    if (todaySchedule) setDates(todaySchedule.schedules);
  }, [schedules]);

  // Update price when product changes
  useEffect(() => {
    const prod = products?.find((p) => p.id === product);
    if (prod && quantity) {
      const calculatedPrice = (cleanNumber(quantity) * prod.price * prod.capacity).toFixed(2);
      handleNewValues({
        ...inputValues,
        price: calculatedPrice,
      });
    }
  }, [product, quantity]);

  // Check credit funds
  useEffect(() => {
    const priceFloat = cleanNumber(price);
    if (priceFloat > creditAccount?.creditAvailability && !requestCredit) {
      setNotFunds(true);
    } else {
      setNotFunds(false);
    }
  }, [quantity, account, price, creditAccount]);

  // Initialize form values
  useEffect(() => {
    if (!account || products.length === 0 || validatedSchedules.length === 0)
      return;

    let productInitial = products[0];
    const isPortable = productInitial.type === "PORTATIL";
    let newValues = { ...inputValues };

    if (!product) {
      newValues.product = productInitial.value;
    }

    if (!date) {
      let schedulesInitial = validatedSchedules[0];
      setDates(schedulesInitial.schedules);
      newValues = {
        ...newValues,
        date: schedulesInitial.value,
        schedule: schedulesInitial?.schedules[0]?.value,
      };
    }

    handleNewValues(newValues);
    setValidateType(isPortable);
  }, [validatedSchedules, products]);

  return {
    // Form
    inputValues,
    handleInputOnChange,
    handleNewValues,
    cleanForm,
    fieldErrors,
    setFieldErrors,
    runValidates,
    checkboxOnChange,
    // Selectors state
    selectedUnit,
    setSelectedUnit,
    selectedBrand,
    setSelectedBrand,
    brandOptions,
    brandLocked,
    setBrandLocked,
    unitsOpt,
    setUnitsOpt,
    dates,
    setDates,
    validatedSchedules,
    notFunds,
    validateType,
    // Computed
    creditAccount,
    disabledCredit,
    limitDays,
    // Redux state
    customer,
    products,
    stationUnits,
    isLoadingComp,
    requestCredit,
    myCallID,
    paymentMethods,
    appliedCoupon,
    // Handlers
    handleDates,
    handleByLts,
    handleByPrice,
    handleObservations,
    handleAccountSelect,
    // Account info
    id,
    alias,
    idStation,
    localPhone,
    type,
    address,
    idCustomer,
    // Dispatch
    dispatch,
    newCustomerService,
    checkCoupon,
  };
};
